import axios, { Axios } from "axios";
import qs from "qs";

/**
 * Echo exposes an expressive API for subscribing to channels and listening
 * for events that are broadcast by Laravel. Echo and event broadcasting
 * allow your team to quickly build robust real-time web applications.
 */
import "./echo";
import { useToast } from "vue-toastification";

declare global {
  interface Window {
    axios: Axios;
    api: Axios;
  }
}

/**
 * Import the layout store
 * This is used to manage the layout state of the application
 */
const storedMode = window.localStorage.getItem("mode");
const parsedMode = storedMode ? JSON.parse(storedMode) : {};
const systemPrefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
const shouldUseDarkMode = !storedMode || parsedMode.colorMode === "dark" || (parsedMode.colorMode === "system" && systemPrefersDark);

if (shouldUseDarkMode) {
  document.documentElement.classList.add("dark");
}

const toast = useToast();

/**
 * We'll load the axios HTTP library which allows us to easily issue requests
 * to our Laravel back-end. This library automatically handles sending the
 * CSRF token as a header based on the value of the "XSRF" token cookie.
 */

const STATUS_MESSAGES: { [key: number]: string } = {
  // 4xx 클라이언트 오류
  400: "잘못된 요청입니다. 요청 내용을 확인해주세요.",
  401: "인증되지 않았습니다. 로그인이 필요합니다.",
  403: "권한이 없습니다.",
  404: "요청한 리소스를 찾을 수 없습니다.",
  405: "허용되지 않은 요청 방법입니다.",
  408: "요청 시간이 초과되었습니다.",
  410: "요청한 리소스가 더 이상 사용되지 않습니다.",
  411: "요청이 길이가 필요합니다.",
  412: "전제 요청 조건이 잘못 되었습니다.",
  413: "파일 크기가 너무 큽니다. 파일 크기를 줄여주세요.",
  414: "요청 URL이 너무 깁니다. URL 길이를 줄여주세요.",
  415: "지원하지 않는 미디어 타입입니다.",
  416: "리소스 범위가 잘못되었습니다.",
  422: "요청이 잘못되었습니다. 요청 내용을 확인해주세요.",
  429: "너무 많은 요청을 보냈습니다. 잠시 후 다시 시도해주세요.",

  // 5xx 서버 오류
  500: "서버 내부 오류가 발생했습니다.",
  502: "게이트웨이 오류가 발생했습니다.",
  503: "서비스를 일시적으로 사용할 수 없습니다.",
  504: "게이트웨이 시간 초과가 발생했습니다.",

  // 6xx 사용자 정의 오류
  600: "오류가 발생하여 처리할 수 없습니다. 고객센터에 문의해주세요.",
};

const handleResponse = (error: any) => {
  const status = error?.response?.status ?? 500;
  let message = error?.response?.data?.message;

  if (!message) {
    message = STATUS_MESSAGES[status];
  }

  if (!message) {
    switch (true) {
      case 400 >= status && status < 500:
        message = STATUS_MESSAGES[400];
        break;
      case 500 <= status:
        message = STATUS_MESSAGES[500];
        break;
      default:
        message = STATUS_MESSAGES[600];
        break;
    }
  }

  status >= 500 ? toast.error(message) : toast.warning(message);
};

window.axios = axios.create({
  baseURL: "/",
  withCredentials: true,
  headers: {
    common: {
      "X-Requested-With": "XMLHttpRequest",
    },
  },
  paramsSerializer: {
    serialize: (params) => qs.stringify(params),
  },
});

window.axios.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error(error);
    return Promise.reject(error);
  },
);

window.addEventListener("unhandledrejection", function (event) {
  const error = event.reason;

  if (axios.isAxiosError(error) && error.response) {
    handleResponse(error);
  } else {
    console.error("Unhandled rejection:", error);
  }
});

window.api = axios.create({
  baseURL: "/api/v1",
  withCredentials: true,
  headers: {
    common: {
      "X-Requested-With": "XMLHttpRequest",
    },
    Accept: "application/vnd.api+json",
  },
  paramsSerializer: {
    serialize: (params) => qs.stringify(params),
  },
});

/**
 * Register the service worker
 */
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/sw.js");
}
